/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderNavigationService,
  CommonActions,
  CommonFlowState, FilterNames, FilterRestService,
  ListActionPipelines,
  UsageDialogConfig,
  usageDialogRoute,
} from '@gh/builder-shared';
import { BuilderPermission, HpeInitType, UsageEntityType } from '@gh/builder-shared-data';
import {
  AppRouter,
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getRouteInitData,
  UserContext,
} from '@gh/common-shared';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import { DialogService, NotificationService, WaitingService } from '@gh/core-ui';
import { cursorTracker, GridRowEvent, newGridPaginationCursor } from '@gh/core-ui-grid';
import { filter$ } from '@gh/rx/operators';

import { omit } from 'lodash';

import {
  HousePlanDetailsDialogConfig,
  HousePlanListBusinessService,
} from '../../resources/house-plan-list-business.service';
import { HousePlanFilter, HousePlanListItem } from '../../resources/house-plans.model';
import { housePlanDetailsDialogRoute } from '../house-plan-details-dialog/house-plan-details-dialog.route';
import { housePlanEstimateCreateDialogRoute } from '../house-plan-estimate-create-dialog/house-plan-estimate-create-dialog.route';
import { HousePlanGridDefinition } from './house-plan-grid-definition.service';

@Component({
  standalone: false,
  selector: 'gh-house-plan-list',
  templateUrl: 'house-plan-list.component.html',
  providers: [HousePlanGridDefinition],
})
export class HousePlanListComponent implements OnInit {
  form: FilterableFormState<HousePlanFilter>;
  dataSource: SlicedDataSource<HousePlanListItem>;
  cursor = newGridPaginationCursor();
  isCloneFromJobAllowed: boolean;
  useSalesEstimates: boolean;

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private routeData: RouteDataService,
              private formStateBuilder: FormStateBuilder,
              public gridDefinition: HousePlanGridDefinition,
              private navigationService: BuilderNavigationService,
              private businessService: HousePlanListBusinessService,
              private userContext: UserContext,
              private waitingService: WaitingService,
              private pipelines: ListActionPipelines,
              private commonActions: CommonActions,
              private notificationService: NotificationService,
              private filterService: FilterRestService,
              private dialogService: DialogService) {
  }

  ngOnInit(): void {
    const { filter: initFilter, gridLayout, housePlanListInit } = getRouteInitData(this.route);
    let isInit = true;
    this.gridDefinition.bind(gridLayout);

    this.isCloneFromJobAllowed = housePlanListInit.isCloneFromJobAllowed;
    this.useSalesEstimates = housePlanListInit.salesEstimate;

    this.form = filterableForm(this.formStateBuilder.buildPlain<HousePlanFilter>({
      value: { ...initFilter },
      children: {
        searchString: void 0,
        quickQuote: { visible: housePlanListInit.quickQuote },
        salesEstimate: { visible: housePlanListInit.salesEstimate },
        showDeleted: void 0,
        showUnusedOnly: void 0,
      },
    }));

    const tracker = this.routeData.tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      if (!isInit) {
        this.filterService.update(FilterNames.housePlans, filter).subscribe();
      }
      isInit = false;
      this.loadData();
    });
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  onCreate(): void {
    this.dialogService.openWithInput(housePlanDetailsDialogRoute, {})
      .afterClosed()
      .subscribe((housePlan) => {
        if (housePlan) {
          this.loadData();
        }
      });
  }

  onEdit(original: HousePlanListItem): void {
    this.dialogService.openWithInput(
      housePlanDetailsDialogRoute,
      <HousePlanDetailsDialogConfig>{ housePlanId: original.housePlanId })
      .afterClosed()
      .subscribe((updated) => {
        if (updated) {
          this.loadData();
        }
      });

  }

  onViewUsage(housePlan: HousePlanListItem): void {
    this.dialogService.openWithInput(usageDialogRoute, <UsageDialogConfig>{
      entityId: housePlan.housePlanId,
      entityType: UsageEntityType.HOUSE_PLAN_USAGE_INFO,
    });
  }

  onDeactivate(housePlan: HousePlanListItem): void {
    const flow = this.pipelines.checkUsageToggleItem({
      messages: {
        confirmUsed: {
          id: 'msg_hp_mark_deleted',
          params: {},
        },
        confirmNotUsed: {
          id: 'msg_hp_delete',
          params: {
            code: housePlan.code,
          },
        },
      },
      executes: {
        checkUsage: this.commonActions.execute((state: CommonFlowState<any>) =>
          this.businessService.checkUsage(state.value)),
        summary: this.commonActions.execute((state: CommonFlowState<any>) =>
          this.businessService.deactivateOrDelete(housePlan.housePlanId, state.value)),
      },
      finish: () => this.loadData(),
    });

    flow.run({
      value: housePlan,
    });
  }

  onActivate(housePlan: HousePlanListItem): void {
    this.businessService.activate(housePlan.housePlanId).pipe(
      this.waitingService.operator())
      .subscribe((res) => this.loadData(), this.notificationService.processAll);
  }

  onEstimateOpen(event: GridRowEvent<HousePlanListItem>): void {
    const { housePlanId } = event.data;
    const { isCloneFromJobAllowed, useSalesEstimates } = this;
    if (event.data.isHasEstimate) {
      this.router.navigateToChild(['/estimating/house-plans', housePlanId, 'estimate'], {
        parentTitle: 'lbl_hp_estimate',
        queryParams: { initType: HpeInitType.Open },
      });
    } else if (this.userContext.hasPermission(BuilderPermission.Estimating.HouseEstimate.Estimate.CREATE_EDIT)) {
      this.dialogService.openWithInput(housePlanEstimateCreateDialogRoute, {
        housePlanId,
        isCloneFromJobAllowed,
        useSalesEstimates,
      }).afterClosed().pipe(
        filter$(Boolean))
        .subscribe((init: any) => this.router.navigateToChild(['/estimating/house-plans', housePlanId, 'estimate', 'new'], {
          parentTitle: 'lbl_hp_estimate',
          queryParams: omit(init, 'housePlanId'),
        }));
    } else {
      this.notificationService.warning('msg_he_empty');
    }
  }

  goToAel(housePlan?: HousePlanListItem): void {
    this.navigationService.goToAel({
      entityId: housePlan ? housePlan.housePlanId : void 0,
      entityType: 'HOUSE_PLAN',
      label: 'lbl_hp_estimate',
    });
  }

  private loadData(): void {
    this.dataSource = this.businessService.findAllAsDataSource(this.form.submitValue);
  }
}
